import classNames from 'classnames/bind';
import styles from './QuizLearning.module.css';
import Select from '../../../components/Select';
import { analysisApiSlice } from '../../../services/analysis';
import { useEffect, useState } from 'react';
import TotalCorrectRate from '../TotalCorrectRate';
import { ALL, BACK, QUIZ_TABLE_HEAD_LIST, VIEW_QUIZ } from '../../../constants';
import { Quiz, QuizChartData, QuizChartList } from '../../../types/analysis';
import Table from '../../../components/Table';
import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import {
  quizChanged,
  quizListChanged,
  quizListPageChanged,
  quizTotalCorrectChanged,
  selectQuizList,
  selectQuizListPage,
  selectQuizTotalCorrect,
} from '../../../features/analysisSlice';
import useModal from '../../../hooks/useModal';

interface QuizLearning {
  projectId: number;
  userId?: number;
  indexSeq: number;
  indexList: QuizChartList;
  hideQuizList: () => void;
}

export default function QuizLearning({
  projectId,
  userId,
  indexSeq,
  indexList,
  hideQuizList,
}: QuizLearning) {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();
  const { toggleModal } = useModal();

  const page = useAppSelector(selectQuizListPage);
  const quizList = useAppSelector(selectQuizList);
  const quizTotalCorrect = useAppSelector(selectQuizTotalCorrect);

  const [isInitialIndex, setIsInitialIndex] = useState(false);
  const [curChapterIndex, setCurChapterIndex] = useState<number>();
  const [curChapterData, setCurChapterData] = useState<QuizChartData>();

  const [getQuizActivityDetails] =
    analysisApiSlice[
      userId ? 'useLazyGetClassQuizDetailsQuery' : 'useLazyGetQuizDetailsQuery'
    ]();

  const getQuizActivity = async () => {
    try {
      const { data } = await getQuizActivityDetails({
        projectId,
        userId,
        page,
        size: 30,
        ...(curChapterData?.seq && { seq: curChapterData.seq }),
      });
      const filteredQuizList = data.quizList.filter(
        ({ contentType, draw }: Quiz) => contentType !== 9 || draw
      );

      dispatch(quizListChanged(filteredQuizList));
      dispatch(quizTotalCorrectChanged(data.totalCorrectRate));
    } catch (error) {
      console.error('Failed to get quiz activity data', error);
    }
  };

  const setIndex = (value: string, i: number) => {
    if (curChapterIndex === i) return;

    const indexData = i
      ? indexList[i - 1]
      : {
          seq: 0,
          content: ALL,
          correctRate: '',
          avgCorrectRate: '',
          message: '',
        };

    if (indexData.message) {
      hideQuizList();
      return;
    }
    setCurChapterIndex(i);
    setCurChapterData(indexData);
    setIsInitialIndex(false);
    dispatch(quizListPageChanged(0));
  };

  const showDetailsModal = (data: Quiz) => {
    const quizIdx = quizList.findIndex(({ seq }) => seq === data.seq);

    dispatch(quizChanged({ seq: data.seq }));
    toggleModal({
      id: 'quiz-details',
      type: 'slider',
      name: VIEW_QUIZ,
      data: { projectId, list: quizList, index: quizIdx },
    });
  };

  useEffect(() => {
    const initialIndexData = indexList.find(({ seq }, i) => {
      const isTrue = seq === indexSeq || !seq;
      if (seq === indexSeq) {
        setCurChapterIndex(i + 1);
      } else if (!seq) {
        setCurChapterIndex(0);
      }
      setIsInitialIndex(isTrue);
      return isTrue;
    });
    setCurChapterData(initialIndexData);
    dispatch(quizChanged({ seq: null, index: null }));
  }, []);

  useEffect(() => {
    if (typeof curChapterIndex === 'number' && !isInitialIndex) {
      getQuizActivity();
    }
  }, [curChapterIndex]);

  useEffect(() => {
    page && getQuizActivity();
  }, [page]);

  if (!quizList.length || !curChapterData) return null;

  return (
    <div className={cx('quiz_detail')}>
      <div className={cx('top')}>
        <div className={cx('option_select')}>
          <div className={cx('btn_wrap')}>
            <button className={cx('btn', 'backspace')} onClick={hideQuizList}>
              <span className={cx('blind')}>{BACK}</span>
            </button>
          </div>
          <Select
            name="quizIndex"
            list={[ALL, ...indexList.map(({ content }) => content)]}
            value={curChapterData.content}
            onClick={setIndex}
            onlyText={indexList.length < 2}
          />
        </div>
        <TotalCorrectRate rate={quizTotalCorrect} />
      </div>
      <Table
        name="quiz-list"
        headList={Object.values(QUIZ_TABLE_HEAD_LIST)}
        bodyList={quizList}
        keyList={Object.keys(QUIZ_TABLE_HEAD_LIST)}
        onClickBody={showDetailsModal}
      />
    </div>
  );
}
